import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useSavedSchemas } from '../hooks/useSavedSchemas';
import axios from 'axios';
import GitHubPushModal from '../components/GitHubPushModal';
import {
  GitBranch, CheckCircle, Download,
  Database, FileCode, Zap, Save,
  FolderOpen, Eye
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { generateAndDownload, previewGeneration } from '../services/api';
import {
  GenerateRequest, GenerateResponse, InputType,
  GeneratorTarget, DatabaseProvider, CiCdTool
} from '../types';

const API_URL =
  process.env.REACT_APP_API_URL ||
  'http://localhost:5062';

const GeneratorPage = () => {
  const {
    isAuthenticated, loginWithRedirect,
    getAccessTokenSilently
  } = useAuth0();

  // ── Saved Schemas ─────────────────────────────
  const {
    schemas, loading: schemasLoading,
    loadSchemas, save: saveSchemaFn,
    update: updateSchemaFn, remove: deleteSchemaFn
  } = useSavedSchemas();

  // ── Modal States ──────────────────────────────
  const [showSaveModal, setShowSaveModal]         = useState(false);
  const [showLoadModal, setShowLoadModal]         = useState(false);
  const [showGitHubModal, setShowGitHubModal]     = useState(false);
  const [schemaName, setSchemaName]               = useState('');
  const [schemaDescription, setSchemaDescription] = useState('');

  // ── Pro + Generation State ────────────────────
  const [isPro, setIsPro]                         = useState(false);
  const [generationsUsed, setGenerationsUsed]     = useState(0);
  const [generationsLimit, setGenerationsLimit]   = useState(5);

  // ── Schema Tracking ───────────────────────────
  const [loadedSchemaId, setLoadedSchemaId]       = useState<number | null>(null);
  const [loadedSchemaName, setLoadedSchemaName]   = useState<string>('');
  const [loadedSchemaType, setLoadedSchemaType]   = useState<'sql' | 'openapi' | null>(null);

  // ── GitHub Push ───────────────────────────────
  const [lastRequest, setLastRequest]             = useState<any>(null);
  const [lastToken, setLastToken]                 = useState<string>('');

  // ── Form State ────────────────────────────────
  const [inputMode, setInputMode]   = useState<'sql' | 'openapi'>('sql');
  const [sqlInput, setSqlInput]     = useState('');
  const [openApiInput, setOpenApiInput] = useState('');
  const [loading, setLoading]       = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]       = useState<GenerateResponse | null>(null);

  const [form, setForm] = useState<GenerateRequest>({
    inputType: InputType.AutoDetect,
    target: GeneratorTarget.DotNet,
    rootNamespace: 'MyCompany.Api',
    rootPackage: 'com.mycompany.api',
    databaseProvider: DatabaseProvider.SqlServer,
    includeDbContext: true,
    includeSwagger: true,
    includeErrorHandling: true,
    ciCdTool: CiCdTool.None
  });

  // ── Computed ──────────────────────────────────
  const isUpdateMode =
    inputMode === 'sql' &&
    !!loadedSchemaId &&
    loadedSchemaType === 'sql';

  const isSaveDisabled =
    inputMode === 'openapi' ||
    (inputMode === 'sql' &&
     loadedSchemaType === 'openapi');

  // ── Load User Data (single call) ──────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadUserData = async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: "https://api.scaffoldbridge.io"
          }
        });

        // ONE call to /api/auth/me gets everything ✅
        const response = await axios.get(
          `${API_URL}/api/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const data = response.data;

        // Set pro status ✅
        const proStatus =
          data.isPro === true ||
          data.plan === 'Pro' ||
          data.planId === 1;

        setIsPro(proStatus);
        setGenerationsUsed(data.generationsUsed ?? 0);

        // -1 = unlimited for Pro ✅
        if (proStatus ||
            data.generationsLimit === -1) {
          setGenerationsLimit(999999);
        } else {
          setGenerationsLimit(
            data.generationsLimit ?? 5);
        }

      } catch (err) {
        console.error('Failed to load user data:', err);
      }
    };

    loadUserData();
  }, [isAuthenticated]);

  // ── Samples ───────────────────────────────────
  const sampleSql = `CREATE TABLE Products (
  Id          INT           NOT NULL PRIMARY KEY,
  ProductName NVARCHAR(100) NOT NULL,
  Price       DECIMAL(18,2) NOT NULL,
  IsActive    BIT           NULL,
  CreatedAt   DATETIME2     NOT NULL
);

CREATE TABLE Categories (
  Id   INT          NOT NULL PRIMARY KEY,
  Name NVARCHAR(50) NOT NULL
);`;

  const sampleOpenApi = JSON.stringify({
    openapi: "3.0.0",
    info: { title: "My API", version: "1.0.0" },
    components: {
      schemas: {
        Product: {
          type: "object",
          required: ["productName", "price"],
          properties: {
            id: { type: "integer" },
            productName: { type: "string", maxLength: 100 },
            price: { type: "number", format: "float" },
            isActive: { type: "boolean", nullable: true }
          }
        }
      }
    },
    paths: {}
  }, null, 2);

  // ── Build Request ─────────────────────────────
  const buildRequest = (): GenerateRequest => {
    if (inputMode === 'sql') {
      return { ...form, inputSql: sqlInput, inputJson: undefined };
    }
    try {
      return { ...form, inputJson: JSON.parse(openApiInput), inputSql: undefined };
    } catch {
      throw new Error('Invalid JSON — please check your OpenAPI input');
    }
  };

  // ── Validate ──────────────────────────────────
  const validate = (): boolean => {
    const input = inputMode === 'sql' ? sqlInput : openApiInput;
    if (!input.trim()) {
      toast.error('Please paste your schema first!');
      return false;
    }
    if (!form.rootNamespace.trim()) {
      toast.error('Root namespace is required!');
      return false;
    }
    if (!form.rootPackage.trim()) {
      toast.error('Root package is required!');
      return false;
    }
    return true;
  };

  // ── Target Change ─────────────────────────────
  const handleTargetChange = (target: number) => {
    const newCiCd =
      target === GeneratorTarget.Java &&
      form.ciCdTool === CiCdTool.AzureDevOps
        ? CiCdTool.None : form.ciCdTool;
    setForm({ ...form, target, ciCdTool: newCiCd });
  };

  // ── CI/CD Helper ──────────────────────────────
  const getCiCdFile = () => {
    if (form.ciCdTool === CiCdTool.GitHubActions)
      return '.github/workflows/build.yml';
    if (form.ciCdTool === CiCdTool.AzureDevOps)
      return 'azure-pipelines.yml';
    return null;
  };

  // ── Generate ──────────────────────────────────
  const handleGenerate = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to generate!');
      loginWithRedirect();
      return;
    }
    if (!validate()) return;
    setLoading(true);

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.scaffoldbridge.io"
        }
      });
      const request = buildRequest();
      setLastRequest(request);
      setLastToken(token);
      await generateAndDownload(request, token);
      setGenerationsUsed(prev => prev + 1);
      toast.success('Project generated! 🎉');

    } catch (err: any) {
      if (err.status === 429 ||
          err.response?.status === 429 ||
          err.message?.toLowerCase()
            .includes('limit')) {
        toast.error(
          '⚡ Generation limit reached! Upgrade to Pro!',
          { duration: 5000 });
        return;
      }
      toast.error(err.message || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Preview ───────────────────────────────────
  const handlePreview = async () => {
    if (!validate()) return;
    setPreviewing(true);
    setPreview(null);
    try {
      const request = buildRequest();
      const result = await previewGeneration(request);
      setPreview(result);
      toast.success(`${result.totalFiles} files ready!`);
    } catch (err: any) {
      toast.error(err.message || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  // ── Upgrade ───────────────────────────────────
  const handleUpgrade = async () => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.scaffoldbridge.io"
        }
      });
      const response = await axios.post(
        `${API_URL}/api/billing/checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch {
      toast.error('Failed to start checkout');
    }
  };

  // ── Save ──────────────────────────────────────
  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save!');
      return;
    }
    if (!sqlInput.trim()) {
      toast.error('No SQL schema to save!');
      return;
    }
    const upper = sqlInput.toUpperCase();
    if (!upper.includes('CREATE TABLE')) {
      toast.error('SQL must contain at least one CREATE TABLE!');
      return;
    }
    const openParens  = (sqlInput.match(/\(/g) || []).length;
    const closeParens = (sqlInput.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      toast.error('SQL has mismatched parentheses!');
      return;
    }

    try {
      if (loadedSchemaId) {
        await updateSchemaFn(loadedSchemaId, {
          name:        loadedSchemaName,
          description: schemaDescription,
          schemaJson:  sqlInput,
          target:      form.target
        });
        toast.success('Schema updated! ✅');
      } else {
        if (!schemaName.trim()) {
          toast.error('Please enter a schema name!');
          return;
        }
        const saved = await saveSchemaFn({
          name:        schemaName,
          description: schemaDescription,
          schemaJson:  sqlInput,
          target:      form.target
        });
        if (saved?.id) {
          setLoadedSchemaId(saved.id);
          setLoadedSchemaName(schemaName);
          setLoadedSchemaType('sql');
        }
        setShowSaveModal(false);
        setSchemaName('');
        setSchemaDescription('');
      }
    } catch { /* handled in hook */ }
  };

  // ── Load ──────────────────────────────────────
  const handleLoadSchema = async (schema: any) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: "https://api.scaffoldbridge.io"
        }
      });
      const response = await axios.get(
        `${API_URL}/api/schema/${schema.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const fullSchema = response.data;
      const schemaType =
        schema.inputType === 'sql' ? 'sql' : 'openapi';

      if (schemaType === 'sql') {
        setInputMode('sql');
        setSqlInput(fullSchema.schemaJson);
      } else {
        try {
          const parsed = JSON.parse(fullSchema.schemaJson);
          setInputMode('openapi');
          setOpenApiInput(JSON.stringify(parsed, null, 2));
        } catch {
          setInputMode('sql');
          setSqlInput(fullSchema.schemaJson);
        }
      }

      setForm(prev => ({
        ...prev, target: fullSchema.target
      }));
      setLoadedSchemaId(schema.id);
      setLoadedSchemaName(schema.name);
      setLoadedSchemaType(schemaType);
      setShowLoadModal(false);
      toast.success(`Loaded: ${schema.name} ✅`);

    } catch {
      toast.error('Failed to load schema!');
    }
  };

  // ── Load from Builder ─────────────────────────
  const location = useLocation();
  useEffect(() => {
    if (location.state?.openApiJson) {
      setInputMode('openapi');
      setOpenApiInput(
        JSON.stringify(
          JSON.parse(location.state.openApiJson),
          null, 2)
      );
      if (location.state?.target !== undefined) {
        setForm(prev => ({
          ...prev, target: location.state.target
        }));
      }
      toast.success('Schema loaded from Builder! 🎉');
    }
  }, [location.state]);

  // ── Render ────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 py-12 px-4">
      <div className="max-w-6xl mx-auto">

        {/* Generation Counter ✅ */}
        {isAuthenticated && (
          <div className="bg-gray-800 border border-gray-700
                          rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">
                Generations this month
              </span>
              <div className="flex items-center gap-2">
                {isPro && (
                  <span className="bg-blue-500/20 text-blue-400
                                   text-xs px-2 py-0.5 rounded-full
                                   font-mono border border-blue-500/30">
                    Pro ⚡
                  </span>
                )}
                <span className="font-mono font-bold
                                 text-sm text-blue-400">
                  {isPro
                    ? `${generationsUsed} used`
                    : `${generationsUsed}/${generationsLimit}`
                  }
                </span>
              </div>
            </div>

            {/* Hide bar + warnings for Pro ✅ */}
            {!isPro && (
              <>
                <div className="w-full bg-gray-700
                                rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full
                                transition-all duration-300 ${
                      generationsUsed >= generationsLimit
                        ? 'bg-red-500'
                        : generationsUsed >=
                          generationsLimit * 0.8
                          ? 'bg-yellow-500'
                          : 'bg-blue-600'
                    }`}
                    style={{
                      width: `${Math.min(
                        (generationsUsed /
                          generationsLimit) * 100,
                        100)}%`
                    }}
                  />
                </div>

                {generationsUsed >= generationsLimit ? (
                  <div className="flex items-center
                                  justify-between mt-2">
                    <p className="text-red-400 text-xs">
                      ⚡ Limit reached!
                    </p>
                    <button
                      onClick={handleUpgrade}
                      className="text-blue-400 hover:text-blue-300
                                 text-xs font-semibold transition-colors">
                      Upgrade to Pro →
                    </button>
                  </div>
                ) : generationsUsed >=
                    generationsLimit * 0.8 ? (
                  <div className="flex items-center
                                  justify-between mt-2">
                    <p className="text-yellow-400 text-xs">
                      ⚠️ {generationsLimit -
                        generationsUsed} remaining
                    </p>
                    <button
                      onClick={handleUpgrade}
                      className="text-blue-400 hover:text-blue-300
                                 text-xs font-semibold transition-colors">
                      Upgrade →
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-blue-200 mb-3">
            Generate Your Project
          </h1>
          <p className="text-gray-500">
            Paste your schema, choose your stack,
            and download instantly.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Left Panel ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Schema Input */}
            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-4 h-4 text-blue-400" />
                <h2 className="text-blue-200 font-semibold">
                  Schema Input
                </h2>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => {
                    if (inputMode === 'openapi') {
                      setSqlInput('');
                      setLoadedSchemaId(null);
                      setLoadedSchemaName('');
                      setLoadedSchemaType(null);
                    }
                    setInputMode('sql');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm
                              font-medium transition-colors ${
                    inputMode === 'sql'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>
                  SQL Schema
                </button>
                <button
                  onClick={() => setInputMode('openapi')}
                  className={`px-4 py-2 rounded-lg text-sm
                              font-medium transition-colors ${
                    inputMode === 'openapi'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}>
                  OpenAPI JSON
                </button>
                <button
                  onClick={() => {
                    if (inputMode === 'sql')
                      setSqlInput(sampleSql);
                    else
                      setOpenApiInput(sampleOpenApi);
                    toast.success('Sample loaded!');
                  }}
                  className="ml-auto px-4 py-2 rounded-lg
                             text-sm text-blue-400 hover:text-blue-300
                             bg-blue-500/10 border border-blue-500/20
                             transition-colors">
                  Load Sample
                </button>
              </div>

              {inputMode === 'sql' ? (
                <textarea
                  value={sqlInput}
                  onChange={e => setSqlInput(e.target.value)}
                  placeholder={`CREATE TABLE Products (\n  Id INT NOT NULL PRIMARY KEY,\n  Name NVARCHAR(100) NOT NULL\n);`}
                  className="w-full h-56 bg-gray-800 border border-gray-700
                             rounded-xl p-4 text-green-400 font-mono text-sm
                             resize-none outline-none focus:border-blue-500
                             transition-colors placeholder-gray-700"
                />
              ) : (
                <textarea
                  value={openApiInput}
                  onChange={e => setOpenApiInput(e.target.value)}
                  placeholder={`{\n  "openapi": "3.0.0",\n  "info": { "title": "My API" }\n}`}
                  className="w-full h-56 bg-gray-800 border border-gray-700
                             rounded-xl p-4 text-green-400 font-mono text-sm
                             resize-none outline-none focus:border-blue-500
                             transition-colors placeholder-gray-700"
                />
              )}
            </div>

            {/* Options */}
            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <FileCode className="w-4 h-4 text-blue-400" />
                <h2 className="text-blue-200 font-semibold">
                  Generator Options
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-2">
                    Target Framework
                  </label>
                  <select
                    value={form.target}
                    onChange={e =>
                      handleTargetChange(Number(e.target.value))}
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               outline-none focus:border-blue-500
                               transition-colors">
                    <option value={0}>.NET 8 Web API</option>
                    <option value={1}>Java Spring Boot (Maven)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-2">
                    Database Provider
                  </label>
                  <select
                    value={form.databaseProvider}
                    onChange={e => setForm({
                      ...form,
                      databaseProvider: Number(e.target.value)
                    })}
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               outline-none focus:border-blue-500
                               transition-colors">
                    <option value={0}>SQL Server</option>
                    <option value={1}>PostgreSQL</option>
                    <option value={2}>MySQL</option>
                    <option value={3}>SQLite</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-2">
                    Root Namespace (.NET)
                  </label>
                  <input
                    type="text"
                    value={form.rootNamespace}
                    onChange={e => setForm({
                      ...form, rootNamespace: e.target.value
                    })}
                    placeholder="MyCompany.Api"
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               font-mono outline-none focus:border-blue-500
                               transition-colors placeholder-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-2">
                    Root Package (Java)
                  </label>
                  <input
                    type="text"
                    value={form.rootPackage}
                    onChange={e => setForm({
                      ...form, rootPackage: e.target.value
                    })}
                    placeholder="com.mycompany.api"
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               font-mono outline-none focus:border-blue-500
                               transition-colors placeholder-gray-600"
                  />
                </div>

                {/* CI/CD */}
                <div className="sm:col-span-2 space-y-3">
                  <label className="block text-gray-400 text-xs
                                    font-mono uppercase tracking-wider">
                    CI/CD Pipeline
                    <span className="ml-2 bg-blue-500/20 text-blue-400
                                     border border-blue-500/20 rounded-full
                                     px-2 py-0.5 text-xs normal-case">
                      Pro ⚡
                    </span>
                  </label>

                  {form.target === GeneratorTarget.DotNet && (
                    <div className="flex gap-2">
                      {[
                        { label: 'None',           value: CiCdTool.None },
                        { label: 'GitHub Actions', value: CiCdTool.GitHubActions },
                        { label: 'Azure DevOps',   value: CiCdTool.AzureDevOps }
                      ].map(opt => (
                        <button key={opt.value}
                          onClick={() => setForm({
                            ...form, ciCdTool: opt.value
                          })}
                          className={`flex-1 py-2.5 rounded-xl text-sm
                                      font-medium border transition-colors ${
                            form.ciCdTool === opt.value
                              ? opt.value === CiCdTool.None
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {form.target === GeneratorTarget.Java && (
                    <div className="flex gap-2">
                      {[
                        { label: 'None',           value: CiCdTool.None },
                        { label: 'GitHub Actions', value: CiCdTool.GitHubActions }
                      ].map(opt => (
                        <button key={opt.value}
                          onClick={() => setForm({
                            ...form, ciCdTool: opt.value
                          })}
                          className={`flex-1 py-2.5 rounded-xl text-sm
                                      font-medium border transition-colors ${
                            form.ciCdTool === opt.value
                              ? opt.value === CiCdTool.None
                                ? 'bg-gray-700 border-gray-600 text-white'
                                : 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'
                          }`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {form.ciCdTool === CiCdTool.GitHubActions && (
                    <div className="bg-green-500/10 border
                                    border-green-500/20 rounded-xl p-3">
                      <p className="text-green-400 text-xs font-semibold">
                        ✅ Zero Setup Required!
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Push to GitHub → pipeline runs automatically!
                      </p>
                    </div>
                  )}
                  {form.ciCdTool === CiCdTool.AzureDevOps && (
                    <div className="bg-yellow-500/10 border
                                    border-yellow-500/20 rounded-xl p-3">
                      <p className="text-yellow-400 text-xs font-semibold">
                        ⚠️ One Time Setup Required
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Link GitHub repo to Azure DevOps once → automatic forever!
                      </p>
                    </div>
                  )}
                  {getCiCdFile() && (
                    <div className="flex items-center gap-2
                                    text-xs font-mono text-yellow-400">
                      <span>📄</span>
                      <span>{getCiCdFile()}</span>
                      <span className="text-gray-600">
                        ← included in ZIP
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Save / Load Buttons */}
            {isAuthenticated && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() =>
                    isUpdateMode
                      ? handleSave()
                      : setShowSaveModal(true)}
                  disabled={isSaveDisabled}
                  title={
                    isSaveDisabled
                      ? 'Cannot save in this mode'
                      : isUpdateMode
                        ? `Update: ${loadedSchemaName}`
                        : 'Save schema'
                  }
                  className={`flex items-center gap-2 border
                              px-4 py-2.5 rounded-xl text-sm
                              font-medium transition-colors ${
                    isSaveDisabled
                      ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
                      : isUpdateMode
                        ? 'bg-green-700 hover:bg-green-600 text-white border-green-600'
                        : 'bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700'
                  }`}>
                  <Save className="w-4 h-4" />
                  {isUpdateMode
                    ? `Update (${loadedSchemaName})`
                    : 'Save Schema'}
                </button>

                <button
                  onClick={() => {
                    loadSchemas();
                    setShowLoadModal(true);
                  }}
                  className="flex items-center gap-2 bg-gray-800
                             hover:bg-gray-700 text-gray-300
                             border border-gray-700 px-4 py-2.5
                             rounded-xl text-sm font-medium
                             transition-colors">
                  <FolderOpen className="w-4 h-4" />
                  Load Schema
                </button>

                {inputMode === 'sql' && loadedSchemaId && (
                  <button
                    onClick={() => {
                      setSqlInput('');
                      setLoadedSchemaId(null);
                      setLoadedSchemaName('');
                      setLoadedSchemaType(null);
                      toast.success('Ready for new schema! ✅');
                    }}
                    className="flex items-center gap-2 bg-gray-800
                               hover:bg-gray-700 text-gray-400
                               border border-gray-700 px-4 py-2.5
                               rounded-xl text-sm font-medium
                               transition-colors">
                    + New Schema
                  </button>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 flex-wrap">
              <button
                onClick={handlePreview}
                disabled={previewing || loading}
                className="flex items-center gap-2 bg-gray-800
                           hover:bg-gray-700 text-gray-300 font-semibold
                           px-6 py-4 rounded-xl border border-gray-700
                           transition-all duration-200
                           disabled:opacity-50 disabled:cursor-not-allowed">
                {previewing
                  ? <LoadingSpinner />
                  : <Eye className="w-4 h-4" />}
                Preview Files
              </button>

              <button
                onClick={handleGenerate}
                disabled={loading || previewing}
                className="flex-1 flex items-center justify-center gap-2
                           bg-blue-600 hover:bg-blue-500 text-white font-bold
                           px-8 py-4 rounded-xl transition-all duration-200
                           hover:shadow-lg hover:shadow-blue-500/25
                           disabled:opacity-50 disabled:cursor-not-allowed">
                {loading
                  ? <><LoadingSpinner /><span>Generating...</span></>
                  : <><Zap className="w-5 h-5" /><span>Generate & Download ZIP</span></>
                }
              </button>

              <button
                onClick={() => setShowGitHubModal(true)}
                disabled={
                  !lastRequest ||
                  (!isPro &&
                   generationsUsed >= generationsLimit)
                }
                title={
                  !lastRequest
                    ? 'Generate a project first'
                    : (!isPro &&
                       generationsUsed >= generationsLimit)
                      ? 'Upgrade to Pro'
                      : 'Push to GitHub'
                }
                className="flex items-center gap-2 bg-gray-800
                           hover:bg-gray-700 text-gray-300
                           border border-gray-700 px-6 py-4
                           rounded-xl font-semibold transition-colors
                           disabled:opacity-40 disabled:cursor-not-allowed">
                <GitBranch className="w-4 h-4" />
                Push to GitHub
              </button>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="space-y-6">
            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl p-6">
              <h3 className="text-blue-200 font-semibold mb-4">
                💡 Quick Tips
              </h3>
              <ul className="space-y-3">
                {[
                  'Click "Load Sample" to try instantly',
                  'SQL input is easiest to start with',
                  'AutoDetect works for both formats',
                  'Generated code is production ready',
                  'ZIP includes SQL migration scripts',
                ].map(tip => (
                  <li key={tip}
                      className="flex items-start gap-2
                                 text-sm text-gray-500">
                    <span className="text-blue-500 mt-0.5 shrink-0">
                      →
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

            {preview && (
              <div className="bg-gray-900 border
                              border-green-800/50 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <h3 className="text-green-400 font-semibold">
                    {preview.totalFiles} Files Ready
                  </h3>
                </div>
                <div className="text-xs text-gray-500 font-mono mb-3">
                  {preview.projectName}/
                </div>
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {preview.generatedFiles.map(file => (
                    <div key={file}
                         className="flex items-center gap-2 text-xs
                                    font-mono text-gray-400
                                    hover:text-gray-300 transition-colors">
                      <span className="text-blue-500">→</span>
                      {file}
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full mt-4 flex items-center justify-center
                             gap-2 bg-green-600 hover:bg-green-500
                             text-white font-semibold py-3 rounded-xl
                             transition-colors disabled:opacity-50">
                  <Download className="w-4 h-4" />
                  Download ZIP
                </button>
              </div>
            )}

            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl p-6">
              <h3 className="text-blue-200 font-semibold mb-4">
                📦 What's Generated
              </h3>
              {form.target === GeneratorTarget.DotNet ? (
                <div className="font-mono text-xs text-gray-500 space-y-1">
                  <div className="text-blue-400">📁 YourProject/</div>
                  <div className="ml-3">📁 Controllers/</div>
                  <div className="ml-6 text-gray-400">└ ProductsController.cs</div>
                  <div className="ml-3">📁 Models/</div>
                  <div className="ml-6 text-gray-400">└ Product.cs</div>
                  <div className="ml-3">📁 Services/</div>
                  <div className="ml-6 text-gray-400">└ ProductService.cs</div>
                  <div className="ml-3">📁 Data/</div>
                  <div className="ml-6 text-gray-400">└ AppDbContext.cs</div>
                  <div className="ml-3">📁 database/</div>
                  <div className="ml-6 text-green-500">└ create_tables.sql ✨</div>
                  {getCiCdFile() && (
                    <div className="ml-3 text-yellow-400">
                      └ {getCiCdFile()} ⚡
                    </div>
                  )}
                </div>
              ) : (
                <div className="font-mono text-xs text-gray-500 space-y-1">
                  <div className="text-blue-400">📁 your-project/</div>
                  <div className="ml-3">📁 controller/</div>
                  <div className="ml-6 text-gray-400">└ ProductController.java</div>
                  <div className="ml-3">📁 model/</div>
                  <div className="ml-6 text-gray-400">└ Product.java</div>
                  <div className="ml-3">📁 service/</div>
                  <div className="ml-6 text-gray-400">└ ProductServiceImpl.java</div>
                  <div className="ml-3 text-gray-400">└ pom.xml</div>
                  <div className="ml-3 text-green-500">└ create_tables.sql ✨</div>
                  {getCiCdFile() && (
                    <div className="ml-3 text-yellow-400">
                      └ {getCiCdFile()} ⚡
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Save Modal ── */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm
                        z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700
                          rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-blue-200 font-semibold text-lg mb-4">
              💾 Save Schema
            </h3>
            <div className="mb-4">
              <label className="block text-gray-400 text-xs font-mono mb-2">
                Schema Name *
              </label>
              <input
                type="text" value={schemaName} autoFocus
                onChange={e => setSchemaName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                placeholder="ECommerce API"
                className="w-full bg-gray-800 border border-gray-700
                           text-gray-200 rounded-xl px-4 py-3 text-sm
                           outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-mono mb-2">
                Description (optional)
              </label>
              <input
                type="text" value={schemaDescription}
                onChange={e => setSchemaDescription(e.target.value)}
                placeholder="Products and categories API"
                className="w-full bg-gray-800 border border-gray-700
                           text-gray-200 rounded-xl px-4 py-3 text-sm
                           outline-none focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSaveModal(false);
                  setSchemaName('');
                  setSchemaDescription('');
                }}
                className="flex-1 py-3 rounded-xl bg-gray-800
                           hover:bg-gray-700 text-gray-400 border
                           border-gray-700 transition-colors text-sm">
                Cancel
              </button>
              <button onClick={handleSave}
                className="flex-1 py-3 rounded-xl bg-blue-600
                           hover:bg-blue-500 text-white font-semibold
                           transition-colors text-sm flex items-center
                           justify-center gap-2">
                <Save className="w-4 h-4" />
                Save Schema
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Load Modal ── */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm
                        z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700
                          rounded-2xl p-6 w-full max-w-lg">
            <h3 className="text-blue-200 font-semibold text-lg mb-4">
              📂 Load Schema
            </h3>
            {schemasLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : schemas.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No saved schemas yet!</p>
                <p className="text-gray-600 text-xs mt-2">
                  Save a schema first to load it here.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
                {schemas.map(schema => {
                  const isSql = schema.inputType === 'sql';
                  return (
                    <div key={schema.id}
                         onClick={() => handleLoadSchema(schema)}
                         className="flex items-center justify-between
                                    bg-gray-800 border border-gray-700
                                    hover:border-blue-500/50 rounded-xl p-4
                                    transition-colors cursor-pointer">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-gray-200 text-sm
                                        font-medium truncate">
                            {schema.name}
                          </p>
                          <span className={`text-xs px-2 py-0.5
                            rounded-full font-mono ${
                            isSql
                              ? 'bg-orange-500/20 text-orange-400'
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {isSql ? 'SQL' : 'OpenAPI'}
                          </span>
                        </div>
                        {schema.description && (
                          <p className="text-gray-500 text-xs
                                        mt-0.5 truncate">
                            {schema.description}
                          </p>
                        )}
                        <p className="text-gray-600 text-xs mt-1">
                          {schema.target === 0
                            ? '.NET 8' : 'Java Spring Boot'}
                          {' · '}
                          {new Date(schema.updatedAt)
                            .toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleLoadSchema(schema);
                          }}
                          className="text-blue-400 hover:text-blue-300
                                     text-xs px-3 py-1.5 rounded-lg
                                     bg-blue-500/10 hover:bg-blue-500/20
                                     transition-colors">
                          Load
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            deleteSchemaFn(schema.id);
                          }}
                          className="text-red-400 hover:text-red-300
                                     text-xs px-3 py-1.5 rounded-lg
                                     bg-red-500/10 hover:bg-red-500/20
                                     transition-colors">
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button
              onClick={() => setShowLoadModal(false)}
              className="w-full py-3 rounded-xl bg-gray-800
                         hover:bg-gray-700 text-gray-400 border
                         border-gray-700 transition-colors text-sm">
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── GitHub Push Modal ── */}
      {showGitHubModal && lastRequest && (
        <GitHubPushModal
          request={lastRequest}
          token={lastToken}
          onClose={() => setShowGitHubModal(false)}
        />
      )}
    </div>
  );
};

export default GeneratorPage;